import React from 'react';
import Box from '@mui/material/Box';
import {Accordion, AccordionDetails, AccordionSummary, Button, Chip, Divider, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import {teaching} from "./meta/teach";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SchoolIcon from '@mui/icons-material/School';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const NAV_COLORS = ['#eef4ff', '#effaf2', '#fff8eb', '#f5f0ff', '#eefbfb', '#f7f7f7'];

function buildInstitutionGroups(items) {
    return items.reduce((groups, item) => {
        if (!groups[item.institution]) {
            groups[item.institution] = [];
        }
        groups[item.institution].push(item);
        return groups;
    }, {});
}

function getPeriodStartYear(period) {
    const match = String(period || "").match(/^(\d{4})/);
    return match ? Number(match[1]) : -1;
}

function getMostRecentYear(courses) {
    return courses.reduce((maxYear, course) => {
        const year = getPeriodStartYear(course.period);
        return year > maxYear ? year : maxYear;
    }, -1);
}

const ACADEMIC_YEAR_START_MONTH = 7; // aout (0-indexe)

function getCurrentAcademicPeriod(date = new Date()) {
    const year = date.getFullYear();
    const month = date.getMonth();
    const startYear = month >= ACADEMIC_YEAR_START_MONTH ? year : year - 1;
    return `${startYear}-${startYear + 1}`;
}

function groupByPeriod(items) {
    return items.reduce((groups, item) => {
        if (!groups[item.period]) {
            groups[item.period] = [];
        }
        groups[item.period].push(item);
        return groups;
    }, {});
}

function sortCourses(courses) {
    return courses.slice().sort((a, b) => {
        const periodCompare = b.period.localeCompare(a.period);
        if (periodCompare !== 0) {
            return periodCompare;
        }
        return a.course.localeCompare(b.course);
    });
}

function institutionToAnchorId(institution) {
    return `institution-${String(institution || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')}`;
}

function CourseEntry({course}) {
    return (
        <Box>
            <Typography variant="subtitle1">{course.course}</Typography>
            <Typography variant="body2" color="text.secondary">
                {course.period} | {course.level}
            </Typography>
            <Typography variant="body2" sx={{mt: 0.5}}>
                {course.description}
            </Typography>
            <Button
                size="small"
                variant="outlined"
                href={course.link || undefined}
                target={course.link ? "_blank" : undefined}
                rel={course.link ? "noreferrer" : undefined}
                disabled={!course.link}
                sx={{mt: 1}}
            >
                Ressources
            </Button>
        </Box>
    );
}

export default function Teaching() {
    const currentAcademicPeriod = getCurrentAcademicPeriod();
    const currentAcademicStartYear = getPeriodStartYear(currentAcademicPeriod);
    const groupedTeaching = buildInstitutionGroups(teaching);
    const institutions = Object.keys(groupedTeaching).sort((a, b) => {
        const mostRecentA = getMostRecentYear(groupedTeaching[a]);
        const mostRecentB = getMostRecentYear(groupedTeaching[b]);

        if (mostRecentA !== mostRecentB) {
            return mostRecentB - mostRecentA;
        }

        return a.localeCompare(b);
    });

    const institutionMenu = institutions.map((institution, index) => ({
        id: institutionToAnchorId(institution),
        label: institution,
        count: groupedTeaching[institution].length,
        color: NAV_COLORS[index % NAV_COLORS.length]
    }));

    return (
        <Box sx={{width: "100%", height: "100%", display: "flex", flexDirection: "column", gap: 2}}>
            <Typography variant="h5">Enseignements</Typography>
            <Typography variant="body2" color="text.secondary">
                Année académique courante: {currentAcademicPeriod}
            </Typography>

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid #1a237e33',
                    background: 'linear-gradient(120deg, #eef4ff 0%, #f5f0ff 100%)'
                }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ color: '#1a237e', display: 'flex' }}>
                        <SchoolIcon />
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e' }}>
                            Plateforme de cours
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Supports, ressources et évaluations des enseignements
                        </Typography>
                    </Box>
                </Stack>
                <Button
                    variant="contained"
                    href="https://polytech.lange.xyz/"
                    target="_blank"
                    rel="noreferrer"
                    endIcon={<OpenInNewIcon />}
                >
                    Accéder à la plateforme
                </Button>
            </Stack>

            <Box sx={{position: 'sticky', top: 8, zIndex: 2, py: 1, backgroundColor: 'background.paper'}}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {institutionMenu.map((item) => (
                        <Chip
                            key={item.id}
                            component="a"
                            clickable
                            href={`#${item.id}`}
                            variant="outlined"
                            label={
                                <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', gap: 0.75}}>
                                    <Box
                                        component="span"
                                        sx={{
                                            width: 9,
                                            height: 9,
                                            borderRadius: '50%',
                                            backgroundColor: item.color,
                                            border: '1px solid rgba(0,0,0,0.18)'
                                        }}
                                    />
                                    {item.label} ({item.count})
                                </Box>
                            }
                            sx={{
                                textDecoration: 'none',
                                borderColor: '#d8dde8',
                                backgroundColor: '#ffffffcc'
                            }}
                        />
                    ))}
                </Stack>
            </Box>

            {institutions.map((institution, institutionIndex) => {
                const allCourses = sortCourses(groupedTeaching[institution]);
                const currentCourses = allCourses.filter((course) => course.period === currentAcademicPeriod);
                const upcomingCourses = allCourses.filter((course) => (
                    course.period !== currentAcademicPeriod
                    && getPeriodStartYear(course.period) > currentAcademicStartYear
                ));
                const oldCourses = allCourses.filter((course) => (
                    course.period !== currentAcademicPeriod
                    && getPeriodStartYear(course.period) <= currentAcademicStartYear
                ));
                const oldByPeriod = groupByPeriod(oldCourses);
                const oldPeriods = Object.keys(oldByPeriod).sort((a, b) => b.localeCompare(a));
                const sectionColor = NAV_COLORS[institutionIndex % NAV_COLORS.length];

                return (
                    <Accordion
                        key={institution}
                        id={institutionToAnchorId(institution)}
                        disableGutters
                        defaultExpanded={currentCourses.length > 0 || upcomingCourses.length > 0}
                        sx={{
                            scrollMarginTop: 72,
                            border: '1px solid #e3e7ee',
                            borderRadius: 1.5,
                            backgroundColor: sectionColor,
                            '&:before': {display: 'none'}
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon/>}
                            sx={{
                                borderBottom: '1px solid #e6ebf3',
                                backgroundColor: '#ffffff66'
                            }}
                        >
                            <Typography variant="h6">
                                {institution} ({allCourses.length})
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="subtitle1" sx={{mb: 1}}>Enseignements courants</Typography>
                            {currentCourses.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                    Aucun cours pour l'année académique courante.
                                </Typography>
                            ) : (
                                <Stack spacing={1.5} sx={{mb: 2}}>
                                    {currentCourses.map((course, index) => (
                                        <Box
                                            key={`${institution}-current-${course.period}-${course.level}-${course.course}`}>
                                            <CourseEntry course={course}/>
                                            {index < currentCourses.length - 1 && <Divider sx={{mt: 1.5}}/>}
                                        </Box>
                                    ))}
                                </Stack>
                            )}

                            {upcomingCourses.length > 0 && (
                                <>
                                    <Typography variant="subtitle1" sx={{mb: 1}}>Enseignements à venir</Typography>
                                    <Stack spacing={1.5} sx={{mb: 2}}>
                                        {upcomingCourses.map((course, index) => (
                                            <Box
                                                key={`${institution}-upcoming-${course.period}-${course.level}-${course.course}`}>
                                                <CourseEntry course={course}/>
                                                {index < upcomingCourses.length - 1 && <Divider sx={{mt: 1.5}}/>}
                                            </Box>
                                        ))}
                                    </Stack>
                                </>
                            )}

                            <Typography variant="subtitle1" sx={{mb: 1}}>Anciens enseignements</Typography>
                            {oldPeriods.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    Aucun ancien enseignements.
                                </Typography>
                            ) : (
                                <Stack spacing={1}>
                                    {oldPeriods.map((period, periodIndex) => {
                                        const periodCourses = sortCourses(oldByPeriod[period]);
                                        return (
                                            <Accordion
                                                key={`${institution}-old-${period}`}
                                                disableGutters
                                                sx={{
                                                    backgroundColor: '#ffffffb3',
                                                    border: '1px solid #e7ebf2',
                                                    borderRadius: 1,
                                                    '&:before': {display: 'none'}
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMoreIcon/>}
                                                    sx={{
                                                        backgroundColor: periodIndex % 2 === 0 ? '#ffffffc9' : '#ffffff99'
                                                    }}
                                                >
                                                    <Typography variant="subtitle2">
                                                        {period} ({periodCourses.length})
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Stack spacing={1.5}>
                                                        {periodCourses.map((course, index) => (
                                                            <Box
                                                                key={`${institution}-old-${period}-${course.level}-${course.course}`}>
                                                                <CourseEntry course={course}/>
                                                                {index < periodCourses.length - 1 &&
                                                                    <Divider sx={{mt: 1.5}}/>}
                                                            </Box>
                                                        ))}
                                                    </Stack>
                                                </AccordionDetails>
                                            </Accordion>
                                        );
                                    })}
                                </Stack>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
}


